import { useState, type ReactNode } from 'react'

import { t } from '@ctcells/i18n'

import { Button } from '../../atoms/Button.tsx'
import { Modal } from '../../atoms/Modal.tsx'
import { SettingsRow } from '../../layout/SettingsList.tsx'
import { Card } from '../resourceParts.tsx'
import { ControlButton, ResultAccepted, ResultFailure } from './ModuleControlRows.tsx'
import type { InitializeGate } from './moduleInitializeGate.ts'

/**
 * 모듈 서랍의 「제어」 카드(eric 2026-10-22 「제어락 필요로 하고. 제어 오른쪽에 정상 칩 두고. 버튼을 … 타이틀만으로 버튼 만들어주고.
 * 마우스 호버를 하면 해당 설명이 보이게 해줘. 제어락 필요는 당연히 다 빼고.」).
 *
 * ```
 * ┌ 제어 (정상)                                         제어락 필요 / [내가 보유] ┐
 * │ [초기화] [파지 기억 초기화] [복구 잠김 풀기] [진단] [트레이 자리 정리]            │  ← 글자만 · 설명은 툴팁
 * │ 점유 중인 자리  거치대 위 · 슬라이드 위   (있을 때만)                             │
 * │ 최근 결과 — 받음 · 거절 사유 · 진단 결과를 한 곳에                                │
 * └──────────────────────────────────────────────────────────────────────────┘
 * ```
 *
 * 🔴 **확인 전에는 요청이 0건이다.** 「초기화」 타일은 확인 창만 연다 — 보내는 것은 창의 「초기화」다.
 * 🔴 **낙관적으로 그리지 않는다.** 202 는 「모듈이 받았다」일 뿐이고, 상태는 모듈 상태 구독이 목록 행에 바꿔 넣는다.
 * 🔴 **실패는 「최근 결과」에 사유와 코드 원문을 같이 적는다**(403 · 409 · 5xx). 확인 창은 누르는 즉시 닫는다 —
 *    창 안에 두면 닫는 순간 사유가 사라진다.
 * ⚠ **관리 › 모듈 장비 제어 화면(`07 §4.11` · `GET /modules/{id}/control`)이 아니다.** 그 화면은 만들지 않는다
 *   (eric 「관리 › 모듈 장비 제어 화면… 이거는.. 하지 마」). 여기는 서랍의 카드 하나다.
 */
export interface ModuleInitializeCardProps {
  /** 확인 창 문장에 넣을 사람이 읽는 이름 */
  moduleName: string
  control: InitializeGate
  /** 보내기. 부르는 즉시 요청이 나간다 */
  onInitialize: () => void
  /** 서버가 202 로 받았다 */
  accepted?: boolean | undefined
  /** 실패 사유(한글) */
  error?: string | undefined
  /** 실패 코드 원문(`error_code`). 없으면 상태 코드 */
  errorCode?: string | undefined
  /** 카드 머리 오른쪽 끝 — 「내가 보유」 칩 또는 「제어락 필요」(컨테이너가 채운다). 없으면 비운다 */
  lock?: ReactNode
  /** 「제어」 제목 바로 오른쪽 상태 칩(`ModuleStatusChips`). 없으면 비운다 */
  status?: ReactNode
  /** 「초기화」 단추 뒤의 다른 조작 단추(파지 기억 · 자동 복구 · 진단). 없으면 비운다 */
  actions?: ReactNode
  /** 단추 줄 끝의 다른 조작(「트레이 자리 정리」 · `ModulePrepareContainer`). 없으면 비운다 */
  extra?: ReactNode
  /**
   * `ModuleSnapshot.handoff_occupancy` 중 `occupied: true` 인 자리의 사람이 읽는 이름(`occupiedPointLabel`).
   * 🔴 단추 줄 아래 한 줄 — 있을 때만 선다. 초기화는 점유를 풀지 않는다(범위 밖) — 푸는 것은 「트레이 자리 정리」다
   */
  occupiedPoints?: readonly string[] | undefined
  /** 「최근 결과」 칸에 «초기화 다음으로» 서는 다른 조작의 결과(컨테이너가 채운다) */
  results?: ReactNode
}

export function ModuleInitializeCard({
  moduleName,
  control,
  onInitialize,
  accepted = false,
  error,
  errorCode,
  lock,
  status,
  actions,
  extra,
  occupiedPoints = [],
  results,
}: ModuleInitializeCardProps) {
  const [confirming, setConfirming] = useState(false)
  const action = t('action.initializeModule')
  const initResult =
    error !== undefined ? (
      <ResultFailure action={action} error={error} errorCode={errorCode} />
    ) : accepted ? (
      <ResultAccepted action={action} text={t('moduleInit.accepted')} />
    ) : null

  return (
    <Card
      title={t('card.moduleControl')}
      titleEnd={
        <>
          {status}
          {lock === undefined ? null : <span className="modctl__lockSlot">{lock}</span>}
        </>
      }
    >
      <div className="modctl__buttons">
        {/*
          ⚠ `advisory`(「모듈이 허용 명령을 안 보냈습니다 — 서버 판정에 맡깁니다」)는 비활성 사유가 아니라 안내다 —
            화면 본문에 적지 않고 툴팁의 설명 뒤로 보낸다(eric 2026-10-22).
        */}
        <ControlButton
          description={t('moduleCtl.desc.initialize')}
          gate={control.gate}
          icon="recover"
          label={action}
          note={control.advisory}
          onClick={() => setConfirming(true)}
        />
        {actions}
        {extra}
      </div>
      <div className="modctl__rows">
        {occupiedPoints.length === 0 ? null : (
          <div className="modctl__occupied">
            <SettingsRow label={t('moduleInit.occupied')} value={occupiedPoints.join(' · ')} />
          </div>
        )}
        <section aria-label={t('moduleCtl.results')} className="modctl__results">
          <SettingsRow
            label={t('moduleCtl.results')}
            value={
              initResult === null && results === undefined ? (
                <span className="muted modctl__noResult">{t('mask.none')}</span>
              ) : (
                <>
                  {initResult}
                  {results}
                </>
              )
            }
          />
        </section>
      </div>

      {confirming ? (
        <Modal
          size="sm"
          footer={
            <>
              <Button onClick={() => setConfirming(false)} size="pill">
                {t('action.cancel')}
              </Button>
              <Button
                size="pill"
                variant="primary"
                {...control.gate}
                onClick={() => {
                  setConfirming(false)
                  onInitialize()
                }}
              >
                {action}
              </Button>
            </>
          }
          onClose={() => setConfirming(false)}
          title={t('moduleInit.confirmTitle')}
        >
          <p>{t('moduleInit.confirmBody', { module: moduleName })}</p>
        </Modal>
      ) : null}
    </Card>
  )
}
